import React, { useEffect, useMemo, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import receptionService from '../../../services/receptionService';
import ReceptionModalShell, { ReceptionModalSection } from '../ui/ReceptionModalShell';
import { BrowseTypePill } from '../browse/BrowseContactLink';
import { memberDisplayName } from '../browse/browseFormatters';
import { ORIENT_LABELS } from '../receptionConstants';
import { formatVisitDate, visitStatusLabel } from '../receptionFormatters';

const TYPE_TONE = {
  AGENT: 'agent',
  CONJOINT: 'partner',
  CONJOINTE: 'partner',
  ENFANT: 'child'
};

function DetailField({ label, value, mono = false }) {
  return (
    <div className="recv-rdv-detail-field">
      <div className="recv-rdv-detail-field__label">{label}</div>
      <div className={`recv-rdv-detail-field__value${mono ? ' font-monospace' : ''}`}>{value || '—'}</div>
    </div>
  );
}

function formatMemberDate(value) {
  if (!value) return '—';
  const s = String(value);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : s.slice(0, 10) || '—';
}

function formatSexe(value) {
  const key = String(value || '').toUpperCase();
  if (key === 'M') return 'Masculin';
  if (key === 'F') return 'Féminin';
  return value || '—';
}

export default function ReceptionMemberDetailModal({
  show,
  member,
  memberTypeLabels = {},
  onHide,
  onEdit
}) {
  const [visits, setVisits] = useState([]);
  const [loadingVisits, setLoadingVisits] = useState(false);
  const [visitsMessage, setVisitsMessage] = useState('');

  useEffect(() => {
    if (!show || !member?.matricule) {
      setVisits([]);
      setVisitsMessage('');
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoadingVisits(true);
      setVisitsMessage('');
      try {
        const res = await receptionService.getVisitHistoryByMatricule(member.matricule, { limit: 8 });
        const rows = res.success && Array.isArray(res.data) ? res.data : [];
        if (cancelled) return;
        setVisits(rows);
        setVisitsMessage(rows.length === 0 ? 'Aucun passage enregistré pour cet adhérent.' : '');
      } catch (e) {
        if (!cancelled) {
          setVisits([]);
          setVisitsMessage(e.message || 'Historique des passages indisponible.');
        }
      } finally {
        if (!cancelled) setLoadingVisits(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show, member?.matricule]);

  const fullName = member ? memberDisplayName(member) : '—';
  const typeLabel = memberTypeLabels[member?.type] || member?.type || '—';
  const typeTone = TYPE_TONE[member?.type] || 'neutral';

  const agentLabel = useMemo(() => {
    if (!member || member.type === 'AGENT') return null;
    const name = [member.agent_prenom, member.agent_nom].filter(Boolean).join(' ').trim();
    if (!name && !member.agent_matricule) return null;
    return [name, member.agent_matricule ? `mat. ${member.agent_matricule}` : null].filter(Boolean).join(' · ');
  }, [member]);

  return (
    <ReceptionModalShell
      show={show}
      onHide={onHide}
      size="lg"
      title={fullName}
      subtitle={member?.matricule ? `Matricule ${member.matricule}` : 'Fiche adhérent'}
      footer={
        <>
          <Button variant="secondary" onClick={onHide}>
            Fermer
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => {
              onEdit?.(member);
              onHide?.();
            }}
          >
            Modifier
          </Button>
        </>
      }
    >
      {!member ? null : (
        <>
          <ReceptionModalSection title="Résumé">
            <div className="recv-rdv-detail-badges mb-3">
              <BrowseTypePill tone={typeTone}>{typeLabel}</BrowseTypePill>
              {member.company_name ? (
                <span className="recv-member-detail-chip">{member.company_name}</span>
              ) : null}
            </div>
            <div className="recv-rdv-detail-grid">
              <DetailField label="Nom" value={member.nom} />
              <DetailField label="Prénom" value={member.prenom} />
              <DetailField label="Matricule" value={member.matricule} mono />
              <DetailField label="Société" value={member.company_name} />
            </div>
          </ReceptionModalSection>

          <ReceptionModalSection title="Identité">
            <div className="recv-rdv-detail-grid">
              <DetailField label="Date de naissance" value={formatMemberDate(member.date_naissance)} />
              <DetailField label="Sexe" value={formatSexe(member.sexe)} />
              <DetailField label="Fonction" value={member.fonction} />
              <DetailField label="Date d'embauche" value={formatMemberDate(member.date_embauche)} />
              {agentLabel ? <DetailField label="Agent rattaché" value={agentLabel} /> : null}
            </div>
          </ReceptionModalSection>

          <ReceptionModalSection title="Derniers passages">
            {loadingVisits ? (
              <div className="recv-member-detail-visits-loading">
                <Spinner animation="border" size="sm" variant="info" />
                <span>Chargement de l&apos;historique…</span>
              </div>
            ) : visits.length > 0 ? (
              <ul className="recv-member-detail-visits list-unstyled mb-0">
                {visits.map((visit) => (
                  <li key={visit.id} className="recv-member-detail-visits__item">
                    <div className="recv-member-detail-visits__head">
                      <strong>{formatVisitDate(visit.registered_at)}</strong>
                      <span className={`recv-member-detail-visits__status${visit.status === 'ORIENTED' ? ' is-ok' : ''}`}>
                        {visitStatusLabel(visit.status)}
                      </span>
                    </div>
                    <div className="recv-member-detail-visits__meta text-muted small">
                      {[
                        visit.service_line ? ORIENT_LABELS[visit.service_line] || visit.service_line : null,
                        visit.visit_site_name,
                        visit.company_name
                      ]
                        .filter(Boolean)
                        .join(' · ') || 'Détails non renseignés'}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted small mb-0">{visitsMessage || 'Aucun passage récent.'}</p>
            )}
          </ReceptionModalSection>
        </>
      )}
    </ReceptionModalShell>
  );
}
